import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { adminApi } from '../../services/adminApi';

export const EnvVarsViewer: React.FC = () => {
  const [envVars, setEnvVars] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.id) {
      loadEnvVars();
    } else {
      setLoading(false);
    }
  }, [user?.id]);

  const loadEnvVars = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await adminApi.getEnvVars();

      if (result.isAuthError) {
        await signOut();
        navigate('/login');
        return;
      }

      if (!result.success) {
        setError(result.error || 'Failed to load environment variables');
        return;
      }

      setEnvVars(result.data?.env || {});
    } catch (err) {
      setError('Failed to load environment variables');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (value: string) => {
    if (value === '(not set)') return '#ef4444';
    if (value.startsWith('http://localhost')) return '#f59e0b';
    return '#22c55e';
  };

  const getStatusLabel = (value: string) => {
    if (value === '(not set)') return 'Missing';
    if (value.startsWith('http://localhost')) return 'Local';
    return 'Set';
  };

  if (loading) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>
        Loading environment variables...
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 800 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>Environment Variables</h2>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: '#9ca3af' }}>
            Edge function environment variables (secrets are masked)
          </p>
        </div>
        <button
          onClick={loadEnvVars}
          style={{
            padding: '8px 16px',
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 14,
          }}
        >
          Refresh
        </button>
      </div>

      {error && (
        <div style={{
          padding: 12,
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: 8,
          color: '#dc2626',
          marginBottom: 16,
          fontSize: 14,
        }}>
          {error}
        </div>
      )}

      <div style={{
        background: 'white',
        borderRadius: 12,
        border: '1px solid #e5e7eb',
        overflow: 'hidden',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>Variable</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>Value</th>
              <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: 13, fontWeight: 600, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(envVars).map(([key, value]) => (
              <tr key={key} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{ padding: '12px 16px', fontSize: 14, fontFamily: 'monospace', fontWeight: 500 }}>
                  {key}
                </td>
                <td style={{ padding: '12px 16px', fontSize: 13, fontFamily: 'monospace', color: value === '(not set)' ? '#ef4444' : '#374151' }}>
                  {value}
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                  <span style={{
                    display: 'inline-block',
                    padding: '2px 10px',
                    borderRadius: 12,
                    fontSize: 12,
                    fontWeight: 500,
                    background: `${getStatusColor(value)}15`,
                    color: getStatusColor(value),
                  }}>
                    {getStatusLabel(value)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p style={{ marginTop: 16, fontSize: 13, color: '#9ca3af' }}>
        URLs are shown in full. Secret keys show only the first and last 4 characters.
        Variables marked "Local" may indicate a misconfigured production secret.
      </p>
    </div>
  );
};
